import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import { SmsHubLayout, StatCard } from "@/components/sms/SmsHubLayout";
import { getSmsOverview } from "@/server/sms.functions";

export const Route = createFileRoute("/dashboard/admin/sms/")({
  head: () => ({ meta: [{ title: "SMS Overview · Admin" }, { name: "robots", content: "noindex" }] }),
  component: Page,
});

function Page() {
  const { loading, primaryRole } = useAuth();
  const fetchOverview = useServerFn(getSmsOverview);
  const { data, isLoading } = useQuery({
    queryKey: ["sms-overview"],
    queryFn: () => fetchOverview(),
    enabled: primaryRole === "admin",
    refetchInterval: 30_000,
  });

  if (loading || primaryRole !== "admin") {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  }

  const c = data as any;
  return (
    <SmsHubLayout title="SMS Overview">
      {isLoading || !c ? (
        <div className="py-8 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Sent today" value={c.sent_today} />
            <StatCard label="Failed today" value={c.failed_today} />
            <StatCard label="Queue scheduled" value={c.queue_scheduled} />
            <StatCard label="Queue sent" value={c.queue_sent} />
            <StatCard label="Replies" value={c.replies} />
            <StatCard label="Opt-outs" value={c.opt_outs} />
            <StatCard label="Suppressed" value={c.suppression} />
            <StatCard label="Campaigns" value={c.campaigns_total} sub={`${c.campaigns_running} running`} />
            <StatCard label="Twilio numbers" value={`${c.numbers_enabled}/${c.numbers_total}`} sub="enabled / total" />
          </div>

          <div className="mt-6 border border-border rounded-md p-5 bg-card">
            <h3 className="text-[14px] font-semibold mb-2">How this works</h3>
            <ol className="text-[13px] text-muted-foreground space-y-1.5 list-decimal pl-5">
              <li>Add Twilio sending numbers in <strong>Twilio</strong> (up to 10).</li>
              <li>Upload an SMS-ready CSV in <strong>Upload</strong>, run matching against the provider directory.</li>
              <li>Review and approve message templates in <strong>Templates</strong> (every body must end with "Reply NO to opt out .").</li>
              <li>Create a campaign in <strong>Campaigns</strong>, link a batch + templates + numbers.</li>
              <li>Generate a dry-run queue in <strong>Send Queue</strong> — no SMS is sent. Inspect scheduling.</li>
              <li>Watch counts in <strong>Analytics</strong>, replies and opt-outs in <strong>Replies</strong>, suppression and audit in <strong>Compliance</strong>.</li>
            </ol>
          </div>
        </>
      )}
    </SmsHubLayout>
  );
}
